import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

export const REQUESTS_QUERY_KEYS = {
  all: ["requests"] as const,
  options: ["requests", "options"] as const,
  topUpAccounts: ["requests", "top-up", "accounts"] as const,
  myTopUps: ["requests", "top-up", "mine"] as const,
  myAccounts: ["requests", "accounts", "mine"] as const,
  adminTopUps: ["requests", "top-up", "admin"] as const,
  adminAccounts: ["requests", "accounts", "admin"] as const,
}

export type RequestUserRef = {
  id: number
  username: string
  role: string
} | null

export type TopUpRequest = {
  id: number
  batch_id: string
  requester_id: number
  requester: RequestUserRef
  tiktok_account_id: number
  advertiser_id: string
  advertiser_name: string
  amount: string | number
  currency: string
  status: "pending" | "approved" | "cancelled" | string
  reviewed_by: number | null
  reviewer: RequestUserRef
  reviewed_at: string | null
  created_at: string | null
  updated_at: string | null
}

export type AccountRequest = {
  id: number
  batch_id: string
  requester_id: number
  requester: RequestUserRef
  network_slug: string
  network_name: string
  agency: string
  top_up_amount: string | number
  status: "pending" | "in_progress" | "completed" | "cancelled" | string
  fulfillment_note: string | null
  reviewed_by: number | null
  reviewer: RequestUserRef
  reviewed_at: string | null
  created_at: string | null
  updated_at: string | null
}

export type TopUpEligibleAccount = {
  id: number
  advertiser_id: string
  advertiser_name: string
  currency: string
  balance: number | null
}

export type RequestOptions = {
  success: boolean
  agencies: string[]
  networks: Array<{ id: number; name: string; slug: string }>
}

type ListResponse<T> = { success: boolean; data: T[] }

export function useRequestOptions() {
  return useQuery<RequestOptions>({
    queryKey: REQUESTS_QUERY_KEYS.options,
    queryFn: () => apiFetch<RequestOptions>("/api/requests/options"),
    staleTime: 60_000,
  })
}

export function useTopUpEligibleAccounts(enabled = true) {
  return useQuery<{ success: boolean; accounts: TopUpEligibleAccount[] }>({
    queryKey: REQUESTS_QUERY_KEYS.topUpAccounts,
    queryFn: () =>
      apiFetch<{ success: boolean; accounts: TopUpEligibleAccount[] }>(
        "/api/requests/top-up/accounts",
      ),
    enabled,
  })
}

export function useMyTopUpRequests({
  enabled = true,
  refetchInterval = 10_000,
}: { enabled?: boolean; refetchInterval?: number | false } = {}) {
  return useQuery<ListResponse<TopUpRequest>>({
    queryKey: REQUESTS_QUERY_KEYS.myTopUps,
    queryFn: () =>
      apiFetch<ListResponse<TopUpRequest>>("/api/requests/top-up/mine"),
    enabled,
    refetchInterval: enabled ? refetchInterval : false,
    refetchIntervalInBackground: false,
  })
}

export function useMyAccountRequests({
  enabled = true,
  refetchInterval = 10_000,
}: { enabled?: boolean; refetchInterval?: number | false } = {}) {
  return useQuery<ListResponse<AccountRequest>>({
    queryKey: REQUESTS_QUERY_KEYS.myAccounts,
    queryFn: () =>
      apiFetch<ListResponse<AccountRequest>>("/api/requests/accounts/mine"),
    enabled,
    refetchInterval: enabled ? refetchInterval : false,
    refetchIntervalInBackground: false,
  })
}

/** Slow poll for sidebar badge while admin is elsewhere. */
export const ADMIN_REQUESTS_IDLE_POLL_MS = 5 * 60_000
/** Faster poll while the Requests page is open. */
export const ADMIN_REQUESTS_ACTIVE_POLL_MS = 15_000

type AdminRequestsQueryOptions = {
  enabled?: boolean
  refetchInterval?: number | false
}

export function useAdminTopUpRequests({
  enabled = true,
  refetchInterval = ADMIN_REQUESTS_ACTIVE_POLL_MS,
}: AdminRequestsQueryOptions = {}) {
  return useQuery<ListResponse<TopUpRequest>>({
    queryKey: REQUESTS_QUERY_KEYS.adminTopUps,
    queryFn: () => apiFetch<ListResponse<TopUpRequest>>("/api/requests/top-up"),
    enabled,
    refetchInterval: enabled ? refetchInterval : false,
    refetchIntervalInBackground: false,
  })
}

export function useAdminAccountRequests({
  enabled = true,
  refetchInterval = ADMIN_REQUESTS_ACTIVE_POLL_MS,
}: AdminRequestsQueryOptions = {}) {
  return useQuery<ListResponse<AccountRequest>>({
    queryKey: REQUESTS_QUERY_KEYS.adminAccounts,
    queryFn: () =>
      apiFetch<ListResponse<AccountRequest>>("/api/requests/accounts"),
    enabled,
    refetchInterval: enabled ? refetchInterval : false,
    refetchIntervalInBackground: false,
  })
}

export function useCreateTopUpRequests() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (items: Array<{ advertiser_id: string; amount: number }>) =>
      apiFetch<ListResponse<TopUpRequest>>("/api/requests/top-up", {
        method: "POST",
        body: { items },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REQUESTS_QUERY_KEYS.all })
    },
  })
}

export function useCreateAccountRequests() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (
      items: Array<{
        network_slug: string
        agency: string
        top_up_amount: number
      }>,
    ) =>
      apiFetch<ListResponse<AccountRequest>>("/api/requests/accounts", {
        method: "POST",
        body: { items },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: REQUESTS_QUERY_KEYS.all })
    },
  })
}

export function useUpdateTopUpRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      id: number
      status: "approved" | "cancelled"
    }) =>
      apiFetch<{ success: boolean; data: TopUpRequest }>(
        `/api/requests/top-up/${data.id}`,
        { method: "PATCH", body: { status: data.status } },
      ),
    onSettled: () => {
      // Refresh even on 409 so the loser sees the winning status.
      void qc.invalidateQueries({ queryKey: REQUESTS_QUERY_KEYS.all })
    },
  })
}

export function useUpdateAccountRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      id: number
      status: "in_progress" | "cancelled" | "completed"
      fulfillment_note?: string
    }) =>
      apiFetch<{ success: boolean; data: AccountRequest }>(
        `/api/requests/accounts/${data.id}`,
        {
          method: "PATCH",
          body: {
            status: data.status,
            fulfillment_note: data.fulfillment_note,
          },
        },
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: REQUESTS_QUERY_KEYS.all })
    },
  })
}

export function useCancelMyTopUpRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ success: boolean; data: TopUpRequest }>(
        `/api/requests/top-up/${id}/cancel`,
        { method: "POST" },
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: REQUESTS_QUERY_KEYS.all })
    },
  })
}

export function useCancelMyAccountRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ success: boolean; data: AccountRequest }>(
        `/api/requests/accounts/${id}/cancel`,
        { method: "POST" },
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: REQUESTS_QUERY_KEYS.all })
    },
  })
}
